import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";

// Stripe payments can take up to ~30s to settle (3DS, webhook propagation).
// Poll for up to 90s total before showing a "still pending" message.
const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 36; // 36 × 2.5s = 90s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  // PayPal subscription redirect: ?subscription_id=I-XXX&ba_token=BA-XXX&token=EC-XXX
  // PayPal one-time order redirect: ?token=ORDER_ID&PayerID=XXX
  // Stripe redirect:                ?session_id=cs_test_XXX | cs_live_XXX
  const stripeSession = params.get("session_id");
  const paypalSubscription = params.get("subscription_id"); // primary PayPal subscription id
  const paypalOrderToken = params.get("token"); // legacy order OR EC token
  const sessionId = stripeSession || paypalSubscription || paypalOrderToken;
  const isStripe = !!stripeSession && stripeSession.startsWith("cs_");
  const { refreshUser } = useAuth();
  const { lang } = useLang();
  const nav = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | pending | failed
  const [attempts, setAttempts] = useState(0);
  const [planInfo, setPlanInfo] = useState(null);
  const [lastError, setLastError] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!sessionId) {
      setStatus("failed");
      setLastError("Missing session_id");
      return;
    }

    let curAttempts = 0;
    let timer = null;
    const statusUrl = isStripe
      ? `/payments/stripe/status/${sessionId}`
      : `/payments/status/${sessionId}`;

    const poll = async () => {
      if (cancelledRef.current) return;
      try {
        const { data } = await api.get(statusUrl);

        // SUCCESS — Stripe: payment_status === "paid" OR "no_payment_required"
        //          PayPal:  payment_status === "paid"
        const paid = data.payment_status === "paid" || data.payment_status === "no_payment_required";
        if (paid) {
          setStatus("paid");
          setPlanInfo({
            plan: data.plan,
            billing: data.billing_period,
            provider: isStripe ? "stripe" : "paypal",
            amount: data.amount_total ? (data.amount_total / 100).toFixed(2) : null,
            currency: data.currency,
          });
          if (typeof refreshUser === "function") {
            try { await refreshUser(); } catch { /* ignore */ }
          }
          // Auto-redirect to dashboard after a brief confirmation pause
          setTimeout(() => {
            if (!cancelledRef.current) nav("/dashboard");
          }, 3500);
          return;
        }

        // EXPLICITLY FAILED — only "expired" is a true terminal failure
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          setLastError(`status=${data.status} payment_status=${data.payment_status}`);
          return;
        }

        // STILL PENDING — keep polling unless we've exhausted attempts
        curAttempts += 1;
        setAttempts(curAttempts);
        if (curAttempts >= MAX_ATTEMPTS) {
          // Don't show "failed" — PayPal/Stripe may still process the payment.
          setStatus("pending");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        const code = e.response?.status;
        // 404 means transaction record not found in our DB — for PayPal subscriptions
        // the backend rebuild may take a second. Retry up to 8 times before giving up.
        if (code === 404 && curAttempts < 8) {
          curAttempts += 1;
          setAttempts(curAttempts);
          timer = setTimeout(poll, POLL_INTERVAL);
          return;
        }
        if (!cancelledRef.current) {
          setStatus("failed");
          setLastError(e.response?.data?.detail || e.message || "Network error");
        }
      }
    };

    poll();
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, refreshUser, isStripe, nav]);

  const tx = (k) => ({
    waiting: { de: "Zahlung wird verarbeitet...", en: "Processing your payment...", tr: "Ödemeniz işleniyor..." },
    waitingSub: { de: "Das kann ein paar Sekunden dauern.", en: "This can take a few seconds.", tr: "Bu birkaç saniye sürebilir." },
    success: { de: "Zahlung erfolgreich!", en: "Payment successful!", tr: "Ödeme başarılı!" },
    successSub: { de: "Dein Plan wurde aktiviert. Willkommen!", en: "Your plan is now active. Welcome aboard!", tr: "Planınız etkinleştirildi. Hoş geldiniz!" },
    redirecting: { de: "Du wirst gleich zum Dashboard weitergeleitet…", en: "Redirecting you to the dashboard…", tr: "Panele yönlendiriliyorsunuz…" },
    failed: { de: "Zahlung fehlgeschlagen", en: "Payment failed", tr: "Ödeme başarısız" },
    failedSub: { de: "Bitte versuche es erneut oder kontaktiere den Support.", en: "Please try again or contact support.", tr: "Lütfen tekrar deneyin veya destekle iletişime geçin." },
    pending: { de: "Zahlung wird noch verarbeitet", en: "Payment still processing", tr: "Ödeme hâlâ işleniyor" },
    pendingSub: {
      de: "Stripe verarbeitet deine Zahlung. Du erhältst eine E-Mail-Bestätigung. Du kannst diese Seite schließen und in 1-2 Minuten zum Dashboard zurückkehren.",
      en: "Stripe is processing your payment. You will receive an email confirmation. You can close this page and check your dashboard in 1-2 minutes.",
      tr: "Stripe ödemenizi işliyor. E-posta onayı alacaksınız. Bu sayfayı kapatabilir, 1-2 dakika içinde panele dönebilirsiniz.",
    },
    toDashboard: { de: "Zum Dashboard", en: "Go to dashboard", tr: "Panele git" },
    backPricing: { de: "Zurück zur Preisseite", en: "Back to pricing", tr: "Fiyatlara dön" },
    refresh: { de: "Status erneut prüfen", en: "Check status again", tr: "Durumu tekrar kontrol et" },
  })[k]?.[lang] || "";

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-white via-purple-50/40 to-white text-zinc-900 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-purple-950/30 dark:to-zinc-950 dark:text-zinc-100 grid place-items-center p-6 relative overflow-hidden"
      data-testid="payment-success-page"
      style={{ marginTop: "-4rem", paddingTop: "4rem" }}
    >
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-purple-600/15 blur-[120px] pointer-events-none" />
      <div className="relative max-w-md w-full text-center">
        {status === "polling" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-600/20 grid place-items-center mb-6">
              <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{tx("waiting")}</h1>
            <p className="mt-2 text-zinc-500 text-sm">{tx("waitingSub")} ({attempts}/{MAX_ATTEMPTS})</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/20 grid place-items-center mb-6">
              <CheckCircle2 className="h-9 w-9 text-emerald-500" />
            </div>
            {planInfo?.plan && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-600 dark:text-purple-300 text-xs font-medium mb-4">
                <Sparkles className="w-3 h-3" />
                {planInfo.plan?.toUpperCase()} {planInfo.billing && `· ${planInfo.billing}`}
                {planInfo.amount && ` · ${planInfo.amount} ${planInfo.currency?.toUpperCase()}`}
              </div>
            )}
            <h1 className="text-3xl font-bold tracking-tight">{tx("success")}</h1>
            <p className="mt-2 text-zinc-500">{tx("successSub")}</p>
            <p className="mt-1 text-xs text-zinc-400" data-testid="success-redirecting">{tx("redirecting")}</p>
            <Button
              onClick={() => nav("/dashboard")}
              className="mt-6 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30 h-11 px-6"
              data-testid="success-to-dashboard"
            >
              {tx("toDashboard")}
            </Button>
          </>
        )}
        {status === "pending" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/20 grid place-items-center mb-6">
              <AlertTriangle className="h-9 w-9 text-amber-500" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{tx("pending")}</h1>
            <p className="mt-3 text-zinc-500 text-sm leading-relaxed">{tx("pendingSub")}</p>
            <div className="flex gap-3 justify-center mt-6">
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="rounded-2xl h-11 px-5"
                data-testid="pending-refresh-btn"
              >
                {tx("refresh")}
              </Button>
              <Button
                onClick={() => nav("/dashboard")}
                className="rounded-2xl bg-purple-600 hover:bg-purple-700 text-white h-11 px-5"
              >
                {tx("toDashboard")}
              </Button>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-500/20 grid place-items-center mb-6">
              <XCircle className="h-9 w-9 text-rose-500" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{tx("failed")}</h1>
            <p className="mt-2 text-zinc-500">{tx("failedSub")}</p>
            {lastError && <p className="mt-3 text-xs text-zinc-400 font-mono break-all">{lastError}</p>}
            <Link to="/pricing">
              <Button className="mt-6 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white h-11 px-6">
                {tx("backPricing")}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
